import { Component } from '@theme/component';

/**
 * <bundle-picker-component>
 * Scent picker for native Shopify "Flex" bundle products (dynamic-bundle
 * template). Owns BOTH the per-slot dropdown interaction and the sync of the
 * bundle selection into the nearest product form as line-item properties
 * (`properties[_components]` + `properties[_Item N]`). Shopify's bundle
 * transform reads `_components` on the added parent line and expands it in the
 * cart (`item.item_components`).
 *
 * This replaces two older pieces that could not survive being cloned into the
 * Quick Add modal:
 *   1. the per-slot inline <script>s in snippets/bundle-picker-dropdown.liquid
 *      (inline scripts don't execute after a morph), and
 *   2. assets/bundle-picker.js (DOMContentLoaded-gated + `.shopify-section`-scoped).
 * As a Component it upgrades automatically wherever it is inserted (PDP or modal)
 * and scopes all queries to its own subtree.
 */
class BundlePickerComponent extends Component {
  /** @type {AbortController | null} */
  #docController = null;

  connectedCallback() {
    super.connectedCallback();

    // Delegated interaction, scoped to this element (not document/index).
    this.addEventListener('click', this.#onClick);

    // Outside-click + Escape close. A fresh controller per connect means a morph
    // re-insert never stacks duplicate document listeners.
    this.#docController = new AbortController();
    const { signal } = this.#docController;
    document.addEventListener('click', this.#onOutsideClick, { signal });
    document.addEventListener('keydown', this.#onKeydown, { signal });

    // Seed the form with the server-rendered default selection. Best-effort: in
    // the Quick Add modal the picker is morphed in ahead of its product form, so
    // this pass often runs before the form exists. #onSubmitCapture is what
    // guarantees the sync actually lands.
    this.#syncComponents();
    document.addEventListener('click', this.#onSubmitCapture, { capture: true, signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#onClick);
    this.#docController?.abort();
    this.#docController = null;
  }

  /**
   * Locate the target product form. Works on the PDP (buy-buttons form in the
   * section) and inside the Quick Add modal (form morphed into the dialog).
   * @returns {HTMLFormElement | null}
   */
  #getForm() {
    const scope = this.closest(
      '.shopify-section, #quick-add-modal-content, .quick-add-modal__content, .product-details'
    );
    return scope?.querySelector('product-form-component form') ?? null;
  }

  /**
   * Re-sync from outside, after a morph has inserted or reused this element.
   * @see assets/quick-add.js
   */
  syncNow() {
    this.#syncComponents();
  }

  /**
   * Submit-time sync, resolved from the live DOM on every click instead of bound
   * to a form looked up once at connect time.
   *
   * The old connect-time binding could not work in the Quick Add modal: the
   * picker is morphed in ahead of its product form, so #getForm() returned null
   * and the binding was skipped and never retried. On the second open the modal
   * already had a picker, so morph reused the element in place and
   * connectedCallback did not run at all - while stripping the hidden inputs the
   * first open had appended. Either way the line was added with no `_components`
   * and the order could not be expanded by the bundle transform.
   *
   * capture:true so this runs before ProductFormComponent.handleSubmit reads FormData.
   * @param {Event} event
   */
  #onSubmitCapture = (event) => {
    if (!(event.target instanceof Element)) return;

    const button = event.target.closest('button[type="submit"], [name="add"]');
    if (!button) return;

    // Only act on our own form - this listener sees every click on the page.
    const form = this.#getForm();
    if (!form || button.closest('form') !== form) return;

    this.#syncComponents();

    // A bundle parent added without components becomes an order nobody can
    // fulfil. If the sync could not resolve a selection, block the add rather
    // than let it through silently.
    if (!form.querySelector('input[name="properties[_components]"]')) {
      event.preventDefault();
      event.stopImmediatePropagation();
      console.error('[bundle-picker] no _components resolved for this bundle; add to cart blocked');
    }
  };

  // -- interaction --------------------------------------------------------

  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const trigger = event.target.closest('[data-bundle-trigger]');
    if (trigger) {
      this.#toggle(trigger);
      return;
    }

    const option = event.target.closest('.bundle-picker__option');
    if (option) this.#select(option);
  };

  /** @param {Element} trigger */
  #toggle(trigger) {
    const slot = trigger.closest('[data-bundle-slot]');
    const panel = slot?.querySelector('[data-bundle-panel]');
    if (!panel) return;

    const open = trigger.getAttribute('aria-expanded') === 'true';
    this.#closeAll();
    if (!open) {
      trigger.setAttribute('aria-expanded', 'true');
      panel.removeAttribute('hidden');
    }
  }

  /** @param {Element} option */
  #select(option) {
    const slot = option.closest('[data-bundle-slot]');
    const trigger = slot?.querySelector('[data-bundle-trigger]');
    const panel = slot?.querySelector('[data-bundle-panel]');
    if (!slot || !trigger || !panel) return;

    const { title, image } = /** @type {HTMLElement} */ (option).dataset;

    const triggerImage = /** @type {HTMLImageElement | null} */ (trigger.querySelector('.bundle-picker__trigger-image'));
    const triggerTitle = trigger.querySelector('.bundle-picker__trigger-title');
    if (triggerImage && image) {
      triggerImage.src = image;
      triggerImage.alt = title || '';
    }
    if (triggerTitle) triggerTitle.textContent = title || '';

    panel.querySelectorAll('.bundle-picker__option').forEach((opt) => {
      opt.classList.remove('bundle-picker__option--selected');
      opt.setAttribute('aria-selected', 'false');
    });
    option.classList.add('bundle-picker__option--selected');
    option.setAttribute('aria-selected', 'true');

    trigger.setAttribute('aria-expanded', 'false');
    panel.setAttribute('hidden', '');

    // Re-sync on every change so the form always carries the current selection.
    this.#syncComponents();
  }

  #closeAll() {
    this.querySelectorAll('[data-bundle-trigger]').forEach((t) => t.setAttribute('aria-expanded', 'false'));
    this.querySelectorAll('[data-bundle-panel]').forEach((p) => p.setAttribute('hidden', ''));
  }

  #onOutsideClick = (event) => {
    if (event.target instanceof Node && !this.contains(event.target)) this.#closeAll();
  };

  #onKeydown = (event) => {
    if (event.key === 'Escape') this.#closeAll();
  };

  // -- form sync ----------------------------------------------------------

  /**
   * Rewrite the bundle line-item-property inputs on the target form from the
   * current per-slot selection. Contract mirrors the legacy bundle-picker.js:
   * `properties[_Item N]` = chosen title, and one `properties[_components]`
   * carrying a JSON array with one entry per slot (per-slot quantity:1, so a
   * repeated scent yields multiple entries — matching prior behavior).
   */
  #syncComponents() {
    const form = this.#getForm();
    if (!form) return;

    form.querySelectorAll('[data-bundle-input]').forEach((el) => el.remove());

    const bundleName = this.dataset.bundleName || '';
    const parentSku = this.dataset.parentSku || '';

    const items = [...this.querySelectorAll('[data-bundle-slot]')]
      .map((slot) => {
        const sel = /** @type {HTMLElement | null} */ (slot.querySelector('.bundle-picker__option--selected'));
        if (!sel) return null;
        return { variantId: sel.dataset.variantId || '', title: sel.dataset.title || '', sku: sel.dataset.sku || '' };
      })
      .filter((it) => it && it.variantId);

    if (items.length === 0) return;

    const bundleSkus = items.map((it) => it.sku);
    const components = items.map((it) => ({
      id: parseInt(it.variantId, 10),
      quantity: 1,
      attributes: {
        _bundle: bundleName,
        _bundle_parent_sku: parentSku,
        _bundle_components: JSON.stringify(bundleSkus),
      },
    }));

    items.forEach((it, i) => this.#appendInput(form, `properties[_Item ${i + 1}]`, it.title));
    this.#appendInput(form, 'properties[_components]', JSON.stringify(components));
  }

  /**
   * @param {HTMLFormElement} form
   * @param {string} name
   * @param {string} value
   */
  #appendInput(form, name, value) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    input.dataset.bundleInput = 'true';
    form.appendChild(input);
  }
}

if (!customElements.get('bundle-picker-component')) {
  customElements.define('bundle-picker-component', BundlePickerComponent);
}
